import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { AlertCircle, Bell, ExternalLink, Flag, Gavel, MessageSquare, X } from "lucide-react";

import { t, useAlertsList, useCommentsList, useDecisionsList, useRedFlagsList } from "@/hooks";
import { useSprintStore, useAuthStore } from "@/store";
import { formatDateTime } from "@/utils";
import { cn } from "@/utils";

export const NotificationBell = () => {
    const [open, setOpen] = useState(false);
    const containerRef = useRef<HTMLDivElement>(null);
    const buttonRef = useRef<HTMLButtonElement>(null);
    const navigate = useNavigate();

    useEffect(() => {
        if (!open) return;
        const handler = (e: MouseEvent) => {
            if (!containerRef.current?.contains(e.target as Node)) setOpen(false);
        };
        document.addEventListener("mousedown", handler);
        return () => document.removeEventListener("mousedown", handler);
    }, [open]);
    const { activeSprintId } = useSprintStore();
    const { user } = useAuthStore();

    const { alerts } = useAlertsList(activeSprintId, { initialPerPage: 5 });
    const { redFlags } = useRedFlagsList(activeSprintId, { initialPerPage: 5 });
    const { decisions } = useDecisionsList(activeSprintId, { initialPerPage: 5 });
    const { comments } = useCommentsList(activeSprintId, { mention: user?.id, initialPerPage: 5 });

    const latestAlert = alerts[0] ?? null;
    const latestRedFlag = redFlags[0] ?? null;
    const latestDecision = decisions[0] ?? null;
    const latestMention = comments[0] ?? null;

    const totalBadge = [latestAlert, latestRedFlag, latestDecision, latestMention].filter(Boolean).length;

    return (
        <div ref={containerRef} className="relative">
            <button
                ref={buttonRef}
                type="button"
                onClick={() => setOpen((o) => !o)}
                className={cn(
                    "relative flex items-center justify-center h-9 w-9 rounded-md transition-colors text-text-muted hover:bg-accent",
                )}
                aria-label={t("Notifications")}
            >
                <Bell className="h-4 w-4" />
                {totalBadge > 0 && (
                    <span className="absolute -top-0.5 -end-0.5 min-w-[16px] h-4 px-1 rounded-full text-[9px] font-bold text-white flex items-center justify-center bg-primary">
                        {totalBadge}
                    </span>
                )}
            </button>

            {open && (
                <>
                    <div className="absolute end-0 top-full mt-2 w-80 bg-surface border border-border rounded-xl shadow-xl z-50 overflow-hidden">
                        <div className="flex items-center justify-between px-4 py-3 border-b border-border">
                            <span className="text-sm font-semibold text-text-dark">{t("Notifications")}</span>
                            <button type="button" onClick={() => setOpen(false)} className="text-text-muted hover:text-text-dark transition-colors">
                                <X className="h-4 w-4" />
                            </button>
                        </div>

                        <div className="divide-y divide-border">
                            {/* Alert */}
                            <NotifRow
                                icon={<AlertCircle className="h-3.5 w-3.5 text-warning" />}
                                label={t("Alert")}
                                labelColor="text-warning"
                                visible={!!latestAlert}
                                title={latestAlert?.title}
                                subtitle={latestAlert?.body}
                                time={latestAlert?.created_at}
                                onNav={() => { navigate("/alerts"); setOpen(false); }}
                                taskId={latestAlert?.source_task?.id}
                                onGoToTask={(id) => { navigate(`/tasks?highlightTask=${id}`); setOpen(false); }}
                            />

                            {/* Red Flag */}
                            <NotifRow
                                icon={<Flag className="h-3.5 w-3.5 text-error" />}
                                label={t("Red Flag")}
                                labelColor="text-error"
                                visible={!!latestRedFlag}
                                title={latestRedFlag?.title}
                                subtitle={latestRedFlag?.description}
                                time={latestRedFlag?.created_at}
                                onNav={() => { navigate("/red-flags"); setOpen(false); }}
                            />

                            {/* Decision */}
                            <NotifRow
                                icon={<Gavel className="h-3.5 w-3.5 text-primary" />}
                                label={t("Decision")}
                                labelColor="text-primary"
                                visible={!!latestDecision}
                                title={latestDecision?.title}
                                subtitle={latestDecision?.description ?? undefined}
                                time={latestDecision?.created_at}
                                onNav={() => { navigate("/decisions"); setOpen(false); }}
                            />

                            {/* Mention */}
                            <NotifRow
                                icon={<MessageSquare className="h-3.5 w-3.5 text-success" />}
                                label={t("Mention")}
                                labelColor="text-success"
                                visible={!!latestMention}
                                title={latestMention?.author?.full_name}
                                subtitle={latestMention?.body}
                                time={latestMention?.created_at}
                                taskId={latestMention?.task_id ?? undefined}
                                onGoToTask={(id) => { navigate(`/tasks?highlightTask=${id}`); setOpen(false); }}
                            />

                            {totalBadge === 0 && (
                                <div className="px-4 py-8 text-center">
                                    <Bell className="h-6 w-6 text-text-muted mx-auto mb-2" />
                                    <p className="text-sm text-text-muted">{t("You're all caught up!")}</p>
                                </div>
                            )}
                        </div>
                    </div>
                </>
            )}
        </div>
    );
};

interface NotifRowProps {
    icon: React.ReactNode;
    label: string;
    labelColor: string;
    visible: boolean;
    title?: string;
    subtitle?: string;
    time?: string;
    onNav?: () => void;
    taskId?: string;
    onGoToTask?: (id: string) => void;
}

const NotifRow = ({ icon, label, labelColor, visible, title, subtitle, time, onNav, taskId, onGoToTask }: NotifRowProps) => {
    if (!visible) return null;
    return (
        <div className="px-4 py-3 hover:bg-muted transition-colors">
            <div className="flex items-center gap-1.5 mb-1">
                {icon}
                <span className={cn("text-[10px] font-bold uppercase tracking-wide", labelColor)}>{label}</span>
            </div>
            <p className="text-xs font-medium text-text-dark line-clamp-1">{title}</p>
            {subtitle && <p className="text-[10px] text-text-muted mt-0.5 line-clamp-2">{subtitle}</p>}
            <div className="flex items-center justify-between mt-1 gap-2">
                {time && <span className="text-[9px] text-text-muted">{formatDateTime(time)}</span>}
                <div className="flex items-center gap-2 ms-auto">
                    {taskId && onGoToTask && (
                        <button type="button" onClick={() => onGoToTask(taskId)} className="inline-flex items-center gap-1 text-[10px] text-warning hover:underline font-medium">
                            <ExternalLink className="h-2.5 w-2.5" />
                            {t("Go to task")}
                        </button>
                    )}
                    {onNav && (
                        <button type="button" onClick={onNav} className="inline-flex items-center gap-1 text-[10px] text-primary hover:underline">
                            <ExternalLink className="h-2.5 w-2.5" />
                            {t("View all")}
                        </button>
                    )}
                </div>
            </div>
        </div>
    );
};

